import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { getBio, updateBio } from '../../../services/api'
import type { Bio, Skill, TimelineItem } from '../../../types/database'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function DashboardBio() {
  const [bio, setBio] = useState<Bio | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadBio()
  }, [])

  const loadBio = async () => {
    setLoading(true)
    const data = await getBio()
    if (!data) {
      setBio({
        id: '1',
        hero_title: '',
        hero_subtitle: '',
        hero_description: '',
        about_title: '',
        about_description: '',
        skills: [],
        timeline: [],
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
    } else {
      setBio(data)
    }
    setLoading(false)
  }

  const handleSave = async () => {
    if (!bio) return
    setSaving(true)
    try {
      await updateBio(bio)
      alert('Bio updated successfully!')
    } catch (error) {
      console.error('Error updating bio:', error)
      alert('Error updating bio')
    } finally {
      setSaving(false)
    }
  }

  const updateField = (field: keyof Bio, value: any) => {
    if (bio) {
      setBio({ ...bio, [field]: value })
    }
  }

  const addSkill = () => {
    if (bio && typeof bio.skills === 'object' && Array.isArray(bio.skills)) {
      const newSkill: Skill = {
        id: crypto.randomUUID(),
        name: '',
        level: 0,
        color: 'from-blue-400 to-blue-600',
        order: bio.skills.length,
      }
      setBio({ ...bio, skills: [...bio.skills, newSkill] })
    } else if (bio) {
      setBio({ ...bio, skills: [{
        id: crypto.randomUUID(),
        name: '',
        level: 0,
        color: 'from-blue-400 to-blue-600',
        order: 0,
      }] })
    }
  }

  const updateSkill = (index: number, field: keyof Skill, value: any) => {
    if (bio && Array.isArray(bio.skills)) {
      const skills = [...bio.skills]
      skills[index] = { ...skills[index], [field]: value }
      setBio({ ...bio, skills } as Bio)
    }
  }

  const removeSkill = (index: number) => {
    if (bio && Array.isArray(bio.skills)) {
      const skills = bio.skills.filter((_, i) => i !== index)
      setBio({ ...bio, skills } as Bio)
    }
  }

  const addTimelineItem = () => {
    if (bio && typeof bio.timeline === 'object' && Array.isArray(bio.timeline)) {
      const newItem: TimelineItem = {
        id: crypto.randomUUID(),
        year: '',
        title: '',
        description: '',
        order: bio.timeline.length,
      }
      setBio({ ...bio, timeline: [...bio.timeline, newItem] })
    } else if (bio) {
      setBio({ ...bio, timeline: [{
        id: crypto.randomUUID(),
        year: '',
        title: '',
        description: '',
        order: 0,
      }] })
    }
  }

  const updateTimelineItem = (index: number, field: keyof TimelineItem, value: any) => {
    if (bio && Array.isArray(bio.timeline)) {
      const timeline = [...bio.timeline]
      timeline[index] = { ...timeline[index], [field]: value }
      setBio({ ...bio, timeline } as Bio)
    }
  }

  const removeTimelineItem = (index: number) => {
    if (bio && Array.isArray(bio.timeline)) {
      const timeline = bio.timeline.filter((_, i) => i !== index)
      setBio({ ...bio, timeline } as Bio)
    }
  }

  if (loading) {
    return <div className="text-text-primary/70">Loading bio...</div>
  }

  if (!bio) {
    return <div className="text-text-primary/70">No bio data found</div>
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <h2 className="text-2xl sm:text-3xl font-semibold text-text-primary">Bio & About</h2>
        <motion.button
          onClick={handleSave}
          disabled={saving}
          className="px-5 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-medium text-text-primary neu-hover touch-manipulation w-full sm:w-auto text-sm sm:text-base disabled:opacity-50"
          whileHover={saving ? {} : { scale: 1.02, y: -1 }}
          whileTap={saving ? {} : { scale: 0.98 }}
          transition={springConfig}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </motion.button>
      </div>

      <div className="space-y-6 sm:space-y-8">
        {/* Hero Section */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">Hero Section</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Hero Title
              </label>
              <input
                type="text"
                value={bio.hero_title || ''}
                onChange={(e) => updateField('hero_title', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="Hey, I'm Peyton Clark"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Hero Subtitle
              </label>
              <input
                type="text"
                value={bio.hero_subtitle || ''}
                onChange={(e) => updateField('hero_subtitle', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="Developer"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                Hero Description
              </label>
              <textarea
                value={bio.hero_description || ''}
                onChange={(e) => updateField('hero_description', e.target.value)}
                rows={3}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation resize-y"
                placeholder="Creating innovative digital experiences..."
              />
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <h3 className="text-xl font-semibold mb-4 text-text-primary">About Section</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                About Title
              </label>
              <input
                type="text"
                value={bio.about_title || ''}
                onChange={(e) => updateField('about_title', e.target.value)}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation"
                placeholder="Crafting Digital Experiences"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                About Description
              </label>
              <textarea
                value={bio.about_description || ''}
                onChange={(e) => updateField('about_description', e.target.value)}
                rows={5}
                className="w-full px-4 sm:px-5 py-3.5 sm:py-4 neu rounded-2xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-base touch-manipulation resize-y"
                placeholder="I'm a passionate developer..."
              />
            </div>
          </div>
        </div>

        {/* Skills Section */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <h3 className="text-xl font-semibold text-text-primary">Skills</h3>
            <motion.button
              onClick={addSkill}
              className="px-4 py-2 neu rounded-xl text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation text-sm font-medium"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              + Add Skill
            </motion.button>
          </div>
          <div className="space-y-4">
            {Array.isArray(bio.skills) && bio.skills.length > 0 ? (
              bio.skills.map((skill, index) => (
                <div key={skill.id || index} className="neu rounded-xl p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                        Skill Name
                      </label>
                      <input
                        type="text"
                        value={skill.name || ''}
                        onChange={(e) => updateSkill(index, 'name', e.target.value)}
                        placeholder="React"
                        className="w-full px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                        Color (optional)
                      </label>
                      <input
                        type="text"
                        value={skill.color || ''}
                        onChange={(e) => updateSkill(index, 'color', e.target.value)}
                        placeholder="from-blue-400 to-blue-600"
                        className="w-full px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs sm:text-sm font-medium text-text-primary/70 uppercase tracking-wide">
                        Skill Level
                      </label>
                      <span className="text-sm sm:text-base font-semibold text-accent">
                        {skill.level || 0}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={skill.level || 0}
                      onChange={(e) => updateSkill(index, 'level', parseInt(e.target.value))}
                      className="w-full h-2 neu rounded-lg appearance-none cursor-pointer touch-manipulation"
                      style={{
                        background: `linear-gradient(to right, rgb(0, 102, 204) 0%, rgb(0, 102, 204) ${skill.level || 0}%, transparent ${skill.level || 0}%, transparent 100%)`
                      }}
                    />
                  </div>
                  <motion.button
                    onClick={() => removeSkill(index)}
                    className="text-red-400 hover:text-red-500 text-sm transition-colors touch-manipulation"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={springConfig}
                  >
                    Remove Skill
                  </motion.button>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-text-primary/50 text-sm mb-4">No skills added yet</p>
                <motion.button
                  onClick={addSkill}
                  className="px-4 py-2 neu rounded-xl text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation text-sm font-medium"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Add Your First Skill
                </motion.button>
              </div>
            )}
          </div>
        </div>

        {/* Timeline Section */}
        <div className="neu rounded-2xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <h3 className="text-xl font-semibold text-text-primary">Journey (Timeline)</h3>
            <motion.button
              onClick={addTimelineItem}
              className="px-4 py-2 neu rounded-xl text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation text-sm font-medium"
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={springConfig}
            >
              + Add Item
            </motion.button>
          </div>
          <div className="space-y-4">
            {Array.isArray(bio.timeline) && bio.timeline.length > 0 ? (
              bio.timeline.map((item, index) => (
                <div key={item.id || index} className="neu rounded-xl p-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                        Year
                      </label>
                      <input
                        type="text"
                        value={item.year || ''}
                        onChange={(e) => updateTimelineItem(index, 'year', e.target.value)}
                        placeholder="2024"
                        className="w-full px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                        Title
                      </label>
                      <input
                        type="text"
                        value={item.title || ''}
                        onChange={(e) => updateTimelineItem(index, 'title', e.target.value)}
                        placeholder="Senior Developer"
                        className="w-full px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs font-medium mb-2 text-text-primary/70 uppercase tracking-wide">
                      Description
                    </label>
                    <textarea
                      value={item.description || ''}
                      onChange={(e) => updateTimelineItem(index, 'description', e.target.value)}
                      placeholder="Leading innovative projects..."
                      rows={2}
                      className="w-full px-4 py-3 neu rounded-xl text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent/30 transition-all font-normal text-sm touch-manipulation resize-y"
                    />
                  </div>
                  <motion.button
                    onClick={() => removeTimelineItem(index)}
                    className="text-red-400 hover:text-red-500 text-sm transition-colors touch-manipulation"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={springConfig}
                  >
                    Remove Item
                  </motion.button>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-text-primary/50 text-sm mb-4">No timeline items added yet</p>
                <motion.button
                  onClick={addTimelineItem}
                  className="px-4 py-2 neu rounded-xl text-accent hover:text-accent-secondary transition-colors neu-hover touch-manipulation text-sm font-medium"
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  transition={springConfig}
                >
                  Add Your First Timeline Item
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
